import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  inject,
  signal,
  computed,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { debounceTime, filter, merge, skip } from "rxjs";
import { RealtimeService } from "../../core/services/chat-realtime.service";
import { BrowserStorageService } from "../../core/services/browser-storage.service";
import { APP_CONSTANTS } from "../../core/constants/app.constants";
import { getDefaultAvatarUrl } from "../../core/constants/avatars.constant";
import { OnboardingComponent } from "../onboarding/onboarding.component";
import { ChatPersistenceService } from "../../core/services/chat-persistence.service";
import {
  GuestSession,
  GuestSessionRequest,
} from "../../core/models/session.model";
import {
  Message,
  MessageBubbleComponent,
} from "./components/message-bubble/message-bubble.component";
import { ComposerComponent } from "./components/composer/composer.component";
import { ChatSidebarComponent } from "./components/chat-sidebar/chat-sidebar.component";
import { MobileTopBarComponent } from "./components/mobile-top-bar/mobile-top-bar.component";
import { ChatHeaderComponent } from "./components/chat-header/chat-header.component";
import { ProfileModalComponent } from "./components/profile-modal/profile-modal.component";
import { CountryInfo } from "../../shared/utils/country-info.util";
import { ChatPageController } from "./chat-page.controller";

export type { Message };

@Component({
  selector: "app-chat-page",
  standalone: true,
  imports: [
    FormsModule,
    OnboardingComponent,
    MessageBubbleComponent,
    ComposerComponent,
    ChatSidebarComponent,
    MobileTopBarComponent,
    ChatHeaderComponent,
    ProfileModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./chat-page.component.html",
  styleUrl: "./chat-page.component.scss",
})
export class ChatPageComponent
  extends ChatPageController
  implements AfterViewChecked
{
  @ViewChild("messagesArea")
  protected messagesArea?: ElementRef<HTMLDivElement>;
  @ViewChild(ComposerComponent) composer?: ComposerComponent;

  private readonly router = inject(Router);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly persistence = inject(ChatPersistenceService);
  protected readonly platformId = inject(PLATFORM_ID);
  protected shouldScrollToBottom = false;
  protected wasAtBottom = true;
  protected messagesResizeObserver?: ResizeObserver;
  protected lastMessagesScrollTop = 0;

  readonly realtime = inject(RealtimeService);
  protected readonly storage = inject(BrowserStorageService);
  readonly storageReady = signal(false);
  readonly onboardingOpen = signal(true);
  readonly draft = signal("");
  readonly customInterest = signal("");
  readonly displayName = signal("Guest");
  readonly avatarUrl = signal<string | null>(null);
  readonly selectedInterests = signal<string[]>([]);
  readonly interests = APP_CONSTANTS.interests;
  readonly isSearching = computed(() => this.realtime.matching());
  readonly blockConfirmation = signal(false);
  readonly messages = signal<Message[]>([]);

  // Dark theme — persisted to localStorage
  readonly darkMode = signal(this.loadDarkMode());

  // Country (own, detected via IP)
  readonly myCountry = signal<string>("");
  readonly country = signal<CountryInfo | null>(null);

  // Profile modal
  readonly profileModalOpen = signal(false);

  // Message actions
  readonly activeMessageMenu = signal<string | null>(null);
  readonly replyTo = signal<{ id: string; text: string } | null>(null);

  // New message pill
  readonly unreadWhileScrolled = signal(0);
  readonly showNewMsgPill = signal(false);

  private readonly seen = new Set<string>();
  private readonly presetInterestSet = new Set<string>(APP_CONSTANTS.interests);
  protected session: GuestSession | null = null;
  protected profile: GuestSessionRequest | null = null;
  // Stores reply context for the next outgoing message echo from backend

  readonly hasDraft = computed(() => this.draft().trim().length > 0);

  constructor() {
    super();
    toObservable(this.realtime.incomingMessage)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((message) => {
        if (this.seen.has(message.id)) return;
        this.seen.add(message.id);
        const msg = this.toMessage(message);
        this.messages.update((items) => [...items, msg]);
        if (this.wasAtBottom) {
          this.shouldScrollToBottom = true;
        } else if (!msg.mine) {
          this.unreadWhileScrolled.update((n) => n + 1);
          this.showNewMsgPill.set(true);
        }
      });

    toObservable(this.realtime.imageViewed)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((messageId) => {
        this.messages.update((items) =>
          items.filter((m) => m.id !== messageId),
        );
      });

    toObservable(this.realtime.peerSkipped)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        this.draft.set("");
        this.startNewChat();
      });

    toObservable(this.realtime.peerLeft)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        this.draft.set("");
        this.startNewChat();
      });

    toObservable(this.realtime.restoreFailed)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        this.persistence.clear();
        this.messages.set([]);
      });

    toObservable(this.realtime.blockCompleted)
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this.draft.set("");
        this.startNewChat();
      });

    toObservable(this.realtime.matched)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => this.blockConfirmation.set(false));

    merge(
      toObservable(this.messages).pipe(skip(1)),
      toObservable(this.realtime.matched),
      toObservable(this.isSearching),
      toObservable(this.realtime.peerSkipped),
      toObservable(this.realtime.name),
      toObservable(this.realtime.avatarUrl),
    )
      .pipe(debounceTime(200), takeUntilDestroyed())
      .subscribe(() => this.persistState());

    // Detect country FIRST (reads localStorage synchronously when cached),
    // so myCountry() is populated before realtime.connect() is called below.
    if (isPlatformBrowser(this.platformId)) {
      this.detectCountry();
    }

    const saved = this.persistence.read();
    if (saved) {
      this.restoreState(saved);
    } else {
      const savedSession = this.persistence.readSession();
      if (savedSession)
        this.restoreProfile(savedSession.session, savedSession.profile);
    }
    if (isPlatformBrowser(this.platformId)) {
      this.storageReady.set(true);
    }

    toObservable(this.realtime.peerIsTyping)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => {
        if (this.wasAtBottom) this.shouldScrollToBottom = true;
      });

    if (isPlatformBrowser(this.platformId)) {
      const saveOnUnload = () => this.persistState();
      window.addEventListener("beforeunload", saveOnUnload);
      this.destroyRef.onDestroy(() => {
        window.removeEventListener("beforeunload", saveOnUnload);
      });
    }

    this.destroyRef.onDestroy(() => {
      this.messagesResizeObserver?.disconnect();
      this.persistState();
      this.realtime.disconnect();
    });
  }

  ngAfterViewChecked(): void {
    this.observeMessagesResize();
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  // ─── Onboarding ───────────────────────────────────────────────────────────
  completeOnboarding(payload: GuestSessionRequest) {
    const userId = crypto.randomUUID();
    const session: GuestSession = {
      userId,
      displayName: payload.displayName,
      interests: payload.interests,
      avatarUrl: payload.avatarUrl ?? getDefaultAvatarUrl(payload.displayName),
    };
    this.session = session;
    this.profile = payload;
    this.persistence.writeSession(session, payload);
    this.displayName.set(session.displayName);
    this.avatarUrl.set(session.avatarUrl ?? null);
    this.onboardingOpen.set(false);
    this.selectedInterests.set(
      payload.interests.slice(0, APP_CONSTANTS.maxInterests),
    );
    this.realtime.connect(
      session.userId,
      session.displayName,
      undefined,
      this.myCountry(),
      session.avatarUrl ?? null,
    );
    this.startNewChat();
  }

  // ─── Profile modal ────────────────────────────────────────────────────────
  openProfileModal() {
    this.profileModalOpen.set(true);
  }

  closeProfileModal() {
    this.profileModalOpen.set(false);
  }

  saveProfile(payload: { name: string; avatarUrl: string | null }) {
    const name = payload.name;
    this.displayName.set(name);
    this.avatarUrl.set(payload.avatarUrl);
    if (this.session) {
      this.session = {
        ...this.session,
        displayName: name,
        avatarUrl: payload.avatarUrl ?? undefined,
      };
      if (this.profile) {
        this.profile = {
          ...this.profile,
          displayName: name,
          avatarUrl: payload.avatarUrl ?? undefined,
        };
        this.persistence.writeSession(this.session, this.profile);
      }
    }
    this.realtime.updateProfile(name, payload.avatarUrl);
    this.profileModalOpen.set(false);
  }

  // ─── Dark mode ────────────────────────────────────────────────────────────
  toggleDarkMode() {
    const next = !this.darkMode();
    this.darkMode.set(next);
    this.storage.setItem("strangeryap:dark-mode", String(next));
  }

  private loadDarkMode(): boolean {
    const saved = this.storage.getItem("strangeryap:dark-mode");
    return saved === null ? true : saved === "true";
  }

  // ─── Interests ────────────────────────────────────────────────────────────
  toggleInterest(value: string) {
    if (this.selectedInterests().includes(value)) return;
    this.selectedInterests.update((items) =>
      [...items, value].slice(0, APP_CONSTANTS.maxInterests),
    );
  }

  addCustomInterest() {
    const value = this.customInterest().trim().replace(/\s+/g, " ");
    if (
      !value ||
      this.selectedInterests().some(
        (i) => i.toLowerCase() === value.toLowerCase(),
      )
    )
      return;
    this.selectedInterests.update((items) =>
      [...items, value].slice(0, APP_CONSTANTS.maxInterests),
    );
    this.customInterest.set("");
  }

  removeInterest(value: string) {
    this.selectedInterests.update((items) => items.filter((i) => i !== value));
  }

  isPresetInterest(value: string): boolean {
    return this.presetInterestSet.has(value);
  }

  // ─── Chat flow ────────────────────────────────────────────────────────────
  startNewChat() {
    this.blockConfirmation.set(false);
    this.messages.set([]);
    this.replyTo.set(null);
    this.realtime.join(this.selectedInterests());
  }

  cancelSearch() {
    this.realtime.cancelSearch();
  }

  skip() {
    this.messages.set([]);
    this.draft.set("");
    this.replyTo.set(null);
    this.realtime.skip(this.selectedInterests());
  }

  block() {
    if (!this.blockConfirmation()) {
      this.blockConfirmation.set(true);
      return;
    }
    this.realtime.blockPeer();
    this.blockConfirmation.set(false);
    this.messages.set([]);
  }

  cancelBlock() {
    this.blockConfirmation.set(false);
  }

  // ─── Messaging ────────────────────────────────────────────────────────────
  send() {
    const text = this.draft().trim().slice(0, APP_CONSTANTS.maxMessageLength);
    if (!text || !this.realtime.matched()) return;
    this.realtime.send(text, this.replyTo());
    this.draft.set("");
    this.replyTo.set(null);
    this.shouldScrollToBottom = true;
  }

  onTyping() {
    this.realtime.emitTyping();
  }

  unsendMessage(id: string) {
    this.messages.update((items) =>
      items.filter((m) => !(m.id === id && m.mine)),
    );
    this.activeMessageMenu.set(null);
  }

  sendImageMessage(imageUrl: string) {
    if (this.realtime.matched()) {
      this.realtime.send("", null, imageUrl);
    }
  }

  viewImage(id: string) {
    const msg = this.messages().find((m) => m.id === id);
    if (msg && !msg.mine) {
      this.realtime.notifyImageViewed(id);
    }
    this.messages.update((items) => items.filter((m) => m.id !== id));
  }

  setReply(msg: Message) {
    this.replyTo.set({ id: msg.id, text: msg.text });
    queueMicrotask(() => this.composer?.focusInput());
    this.activeMessageMenu.set(null);
  }

  cancelReply() {
    this.replyTo.set(null);
  }

  toggleMessageMenu(id: string) {
    this.activeMessageMenu.update((cur) => (cur === id ? null : id));
  }

  closeMessageMenu() {
    this.activeMessageMenu.set(null);
  }

  scrollToMessage(id: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = document.getElementById(`msg-${id}`);
    el?.scrollIntoView({ behavior: "auto", block: "center" });
  }

  // ─── Scroll / new-message pill ────────────────────────────────────────────
  onMessagesScroll(event: Event) {
    const el = event.target as HTMLDivElement;
    this.lastMessagesScrollTop = el.scrollTop;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    this.wasAtBottom = atBottom;
    if (atBottom) {
      this.showNewMsgPill.set(false);
      this.unreadWhileScrolled.set(0);
    }
  }

  scrollToLatest() {
    this.scrollToBottom();
    this.showNewMsgPill.set(false);
    this.unreadWhileScrolled.set(0);
  }

  home() {
    this.router.navigateByUrl("/");
  }

  protected startNewChatFromRestore(): void {
    this.startNewChat();
  }
}
